import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { api, formatError, waDefault, PHONE_DISPLAY } from "@/lib/api";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const inputCls =
  "w-full rounded-lg border border-gold/20 bg-ink/70 px-4 py-3 text-ivory placeholder:text-stone2/60 outline-none transition-colors focus:border-gold";

const ContactForm = ({ about = "" }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [waUrl, setWaUrl] = useState(waDefault());

  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr("");
    try {
      const { data } = await api.post("/contact", { ...form, property_title: about });
      setWaUrl(data.wa_url);
      setSent(true);
      window.open(data.wa_url, "_blank");
    } catch (error) {
      setErr(formatError(error.response?.data?.detail) || "No se pudo enviar. Escríbenos directamente por WhatsApp.");
    }
    setSending(false);
  };

  if (sent)
    return (
      <div className="rounded-2xl border border-wa/30 bg-wa/10 p-8 text-center" data-testid="contact-success-panel">
        <CheckCircle2 className="mx-auto text-wa" size={44} />
        <h3 className="mt-4 font-serif text-2xl text-ivory">¡Mensaje preparado!</h3>
        <p className="mt-2 text-sm text-stone2">
          Se abrió WhatsApp con tu mensaje. Si no se abrió, pulsa el botón de abajo y te atendemos al momento.
        </p>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-reopen-whatsapp-button"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3 font-semibold text-ink transition-transform hover:-translate-y-0.5"
        >
          <WhatsAppIcon className="h-5 w-5" /> Abrir WhatsApp {PHONE_DISPLAY}
        </a>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <input
          required
          placeholder="Tu nombre *"
          value={form.name}
          onChange={set("name")}
          data-testid="contact-input-name"
          className={inputCls}
        />
        <input
          required
          type="tel"
          placeholder="Tu teléfono *"
          value={form.phone}
          onChange={set("phone")}
          data-testid="contact-input-phone"
          className={inputCls}
        />
      </div>
      <input
        type="email"
        placeholder="Tu email (opcional)"
        value={form.email}
        onChange={set("email")}
        data-testid="contact-input-email"
        className={inputCls}
      />
      <textarea
        rows={4}
        placeholder="Cuéntanos qué buscas: visitas, dudas, vender tu propiedad…"
        value={form.message}
        onChange={set("message")}
        data-testid="contact-input-message"
        className={inputCls}
      />
      {err && (
        <p className="text-sm text-red-400" data-testid="contact-error">
          {err}
        </p>
      )}
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-submit-button"
        className="btn-gold-metallic flex w-full items-center justify-center gap-3 rounded-full py-4 text-sm font-bold uppercase tracking-[0.2em] text-ink disabled:opacity-60"
      >
        {sending ? "Enviando…" : "Enviar y abrir WhatsApp"}
        <Send size={16} />
      </button>
      <p className="text-center text-xs text-stone2">
        Al enviar se abrirá WhatsApp con tu mensaje listo para {PHONE_DISPLAY}. Sin registros, sin esperas.
      </p>
    </form>
  );
};

export default ContactForm;
